import { translateLabel } from "@/i18n/messages";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { legal, legalAddressLines } from "@/config/legal";

const updatedAt = translateLabel("29 September 2026");

function LegalLayout({ eyebrow, title, children }) {
  return (
    <main className="min-h-screen bg-[#09090b] text-white">
      <Navbar />
      <article className="mx-auto max-w-3xl px-5 pb-16 pt-28 sm:px-8 md:pt-32">
        <p className="label-tiny text-[#CCFF00]">{eyebrow}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold uppercase sm:text-5xl">{title}</h1>
        <p className="mt-4 text-sm text-zinc-500">{translateLabel("Last updated: {date}", { date: updatedAt })}</p>
        <div className="mt-10 space-y-9 text-sm leading-7 text-zinc-300 sm:text-base">
          {children}
        </div>
      </article>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-extrabold uppercase text-white">{title}</h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout eyebrow={translateLabel("Legal")} title={translateLabel("Privacy Policy")}>
      <Section title={translateLabel("Who is responsible")}>
        <p>{translateLabel("{business} is responsible for processing personal data on Bikeparks Finder.", { business: legal.businessName })}</p>
        <address className="not-italic text-zinc-200">
          <p>{legal.businessName}</p>
          <p>{legal.responsiblePerson}</p>
          {legalAddressLines.map((line) => <p key={line}>{translateLabel(line)}</p>)}
          <p className="mt-3"><a className="text-[#CCFF00] hover:underline" href={`mailto:${legal.email}`}>{legal.email}</a></p>
        </address>
      </Section>

      <Section title={translateLabel("Data we process")}>
        <p>{translateLabel("We process the data needed to provide Bikeparks Finder, keep it secure and respond to requests. This can include technical information such as IP address, browser and device information, pages requested, timestamps and server log data.")}</p>
        <p>{translateLabel("If you create an account, we process the account details you provide, such as your name, email address and authentication information. Information you choose to add to your rider profile, saved places, maps or submitted content is processed to provide those features.")}</p>
      </Section>

      <Section title={translateLabel("Analytics")}>
        <p>{translateLabel("Google Analytics is optional. It is not loaded until you choose “Allow analytics” in the analytics preference notice. If you allow it, Google Analytics receives usage and device information to help us understand which parks, trails and features are useful. You can decline it; the core service remains available.")}</p>
      </Section>

      <Section title={translateLabel("Why we use data")}>
        <p>{translateLabel("We use personal data to provide and secure the service, operate accounts and user-requested features, communicate about support or account matters, and improve the product where analytics consent has been given.")}</p>
      </Section>

      <Section title={translateLabel("Your choices and rights")}>
        <p>{translateLabel("You may request access, correction, deletion, restriction of processing, or a copy of your personal data, and you may object to processing where applicable. Where processing is based on consent, you can withdraw consent at any time with future effect. Contact us at")} <a className="text-[#CCFF00] hover:underline" href={`mailto:${legal.email}`}>{legal.email}</a>.</p>
        <p>{translateLabel("You also have the right to lodge a complaint with a competent data protection supervisory authority.")}</p>
      </Section>

      <Section title={translateLabel("Changes")}>
        <p>{translateLabel("We may update this policy when the service or its processing changes. The current version is always published on this page.")}</p>
      </Section>
    </LegalLayout>
  );
}

export function TermsOfUse() {
  return (
    <LegalLayout eyebrow={translateLabel("Legal")} title={translateLabel("Terms of Use")}>
      <Section title={translateLabel("Service")}>
        <p>{translateLabel("Bikeparks Finder helps riders discover bike parks, trails and related riding information. The service is provided for general information and planning purposes.")}</p>
      </Section>

      <Section title={translateLabel("Ride responsibly")}>
        <p>{translateLabel("Trail conditions, opening times, access rules, prices and safety requirements can change without notice. Always check the official operator information, local rules and weather conditions before riding. You are responsible for your equipment, decisions and safe participation.")}</p>
      </Section>

      <Section title={translateLabel("Accounts and contributions")}>
        <p>{translateLabel("Keep your account credentials confidential and provide accurate information. If you submit content or suggest a destination, you must have the right to share it and must not submit unlawful, misleading or infringing material.")}</p>
      </Section>

      <Section title={translateLabel("Third-party information")}>
        <p>{translateLabel("The service may link to or display information from third parties. We do not control third-party websites or operators and cannot guarantee that their information is complete, current or accurate.")}</p>
      </Section>

      <Section title={translateLabel("Availability and changes")}>
        <p>{translateLabel("We may change, suspend or discontinue features when reasonably necessary. We aim to keep the service useful, but do not guarantee uninterrupted availability or that every listing is complete or current.")}</p>
      </Section>

      <Section title={translateLabel("Contact")}>
        <p>{translateLabel("Questions about these terms can be sent to")} <a className="text-[#CCFF00] hover:underline" href={`mailto:${legal.email}`}>{legal.email}</a>.</p>
      </Section>
    </LegalLayout>
  );
}

export function Impressum() {
  return (
    <LegalLayout eyebrow={translateLabel("Legal")} title={translateLabel("Impressum")}>
      <Section title="Angaben gemäß § 5 DDG">
        <address className="not-italic">
          <p className="font-semibold text-white">{legal.businessName}</p>
          <p>Inhaber: {legal.responsiblePerson}</p>
          {legalAddressLines.map((line) => <p key={line}>{line}</p>)}
        </address>
      </Section>

      <Section title="Kontakt">
        <p>E-Mail: <a className="text-[#CCFF00] hover:underline" href={`mailto:${legal.email}`}>{legal.email}</a></p>
      </Section>

      <Section title="Verantwortlich für den Inhalt">
        <p>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV: {legal.responsiblePerson}, {legalAddressLines.join(", ")}.</p>
      </Section>

      <Section title="Hinweis">
        <p>Dieses Impressum gilt für die Website und die Progressive Web App Bikeparks Finder.</p>
        <p><Link to="/privacy" className="text-[#CCFF00] hover:underline">Datenschutzerklärung</Link></p>
      </Section>
    </LegalLayout>
  );
}
